import { useCallback, useMemo } from "react";
import { publicApi, siteApi } from "@api";
import { QUERY_ALL } from "@constants";
import useUserMode from "@hooks/useUserMode";
import type {
  GetGameListRequest,
} from "kz-ui-sdk";

/**
 * Hook to get game data source based on user mode
 * OPEN mode will use public API
 * MEMBER mode will use site API
 * Automatically handles the 403 fallback from MEMBER to OPEN on getting game list
 */
const useGameDataSource = () => {
  const { isGuest, isMember } = useUserMode();

  // MEMBER MODE
  const { data: memberProvidersResponse } = siteApi.useGetGameProviderListQuery(
    {
      query: QUERY_ALL,
    },
    {
      skip: isGuest,
    },
  );
  const { data: memberCategoriesResponse, isFetching: isFetchingMemberGameCategory } =
    siteApi.useGetGameCategoryListQuery(
      {
        query: QUERY_ALL,
      },
      {
        skip: isGuest,
      },
    );
  const { data: memberProvidersStatsResponse, isFetching: isFetchingMemberProviderStats } =
    siteApi.useGetGameProviderStatsQuery(undefined, {
      skip: isGuest,
    });
  const [getMemberGameList] = siteApi.useLazyGetGameListQuery();

  // OPEN MODE
  const { data: publicProvidersResponse } = publicApi.useGetPublicGameProviderListQuery(
    {
      query: QUERY_ALL,
    },
    {
      skip: isMember,
    },
  );
  const { data: publicCategoriesResponse, isFetching: isFetchingPublicGameCategory } =
    publicApi.useGetPublicGameCategoryListQuery(
      {
        query: QUERY_ALL,
      },
      {
        skip: isMember,
      },
    );
  const { data: publicProvidersStatsResponse, isFetching: isFetchingPublicProviderStats } =
    publicApi.useGetPublicGameProviderStatsQuery(undefined, {
      skip: isMember,
    });
  const [getPublicGameList] = publicApi.useLazyGetPublicGameListQuery();

  // Fallback-aware trigger function for getGameList
  const getGameListWithFallback = useCallback(
    async (params: GetGameListRequest) => {
      if (!isMember) {
        return getPublicGameList(params);
      }
      try {
        return getMemberGameList(params);
      } catch (err: any) {
        if (err.status === 403) {
          return getPublicGameList(params);
        }
        throw err;
      }
    },
    [isMember, getMemberGameList, getPublicGameList],
  );

  // combine the data
  const gameData = useMemo(() => {
    let providersResponse = null;
    let categoriesResponse = null;
    let providersStatsResponse = null;
    let isFetchingGameCategory = false;
    let isFetchingProviderStats = false;

    if (isMember) {
      providersResponse = memberProvidersResponse;
      categoriesResponse = memberCategoriesResponse;
      providersStatsResponse = memberProvidersStatsResponse;
      isFetchingGameCategory = isFetchingMemberGameCategory;
      isFetchingProviderStats = isFetchingMemberProviderStats;
    }

    if (isGuest) {
      providersResponse = publicProvidersResponse;
      categoriesResponse = publicCategoriesResponse;
      providersStatsResponse = publicProvidersStatsResponse;
      isFetchingGameCategory = isFetchingPublicGameCategory;
      isFetchingProviderStats = isFetchingPublicProviderStats;
    }

    return {
      providersResponse,
      categoriesResponse,
      providersStatsResponse,
      isFetchingGameCategory,
      isFetchingProviderStats,
      getGameList: getGameListWithFallback,
    };
  }, [
    isFetchingMemberGameCategory,
    isFetchingMemberProviderStats,
    isFetchingPublicGameCategory,
    isFetchingPublicProviderStats,
    memberCategoriesResponse,
    memberProvidersResponse,
    memberProvidersStatsResponse,
    publicCategoriesResponse,
    publicProvidersResponse,
    publicProvidersStatsResponse,
    isMember,
    isGuest,
    getGameListWithFallback,
  ]);

  return gameData;
};

export default useGameDataSource;
